import {
    AGENT_INSTRUCTIONS,
    COMPONENTS,
    SHAPE,
    SPACING_STEPS,
    STYLE_GUIDE_SLUG,
    STYLE_GUIDE_TITLE,
    STYLE_GUIDE_VERSION,
    SWATCHES,
    TYPE_SCALE,
    USAGE_RULES,
} from './catalog'

export type StyleGuideDownloadKind = 'markdown' | 'xml' | 'excel' | 'pdf'

const FILENAMES: Record<StyleGuideDownloadKind, string> = {
    markdown: `${STYLE_GUIDE_SLUG}.md`,
    xml: `${STYLE_GUIDE_SLUG}.xml`,
    excel: `${STYLE_GUIDE_SLUG}.xls`,
    pdf: `${STYLE_GUIDE_SLUG}.pdf`,
}

const MIME: Record<StyleGuideDownloadKind, string> = {
    markdown: 'text/markdown;charset=utf-8',
    xml: 'application/xml;charset=utf-8',
    excel: 'application/vnd.ms-excel;charset=utf-8',
    pdf: 'application/pdf',
}

export const downloadStyleGuide = (kind: StyleGuideDownloadKind) => {
    const filename = FILENAMES[kind]
    if (kind === 'pdf') {
        triggerDownload(filename, MIME[kind], buildStyleGuidePdf())
        return
    }
    const body = kind === 'markdown' ? buildStyleGuideMarkdown() : kind === 'xml' ? buildStyleGuideXml() : buildStyleGuideExcel()
    triggerDownload(filename, MIME[kind], body)
}

export const triggerDownload = (filename: string, mime: string, body: string | Uint8Array) => {
    const blob = body instanceof Uint8Array ? new Blob([body], { type: mime }) : new Blob([body], { type: mime })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
}

export const buildStyleGuideMarkdown = (): string => {
    const colourLines = SWATCHES.flatMap((group) => [
        `### ${group.group}`,
        '',
        '| Token | Hex |',
        '| --- | --- |',
        ...group.items.map(([name, hex]) => `| \`${name}\` | \`${hex}\` |`),
        '',
    ])

    return [
        `# ${STYLE_GUIDE_TITLE}`,
        '',
        `Version ${STYLE_GUIDE_VERSION}. Living reference for \`src/ui\` in sentinel-dashboard.`,
        '',
        '## Instructions for coding agents',
        '',
        ...AGENT_INSTRUCTIONS.map((line) => `- ${line}`),
        '',
        '## Usage rules',
        '',
        '| Topic | Rule |',
        '| --- | --- |',
        ...USAGE_RULES.map(([topic, rule]) => `| ${topic} | ${rule} |`),
        '',
        '## Colour tokens',
        '',
        'Slate accent, one grey ladder, muted semantic colours. Never write a hex literal in a page module.',
        '',
        ...colourLines,
        '## Typography',
        '',
        `Font: ${SHAPE.font}. Mono: ${SHAPE.fontMono}.`,
        '',
        '| Role | Spec | Sample |',
        '| --- | --- | --- |',
        ...TYPE_SCALE.map((row) => `| ${row.label} | ${row.meta} | ${row.sample} |`),
        '',
        '## Spacing and shape',
        '',
        `- Spacing scale (px): ${SPACING_STEPS.join(', ')}`,
        `- Card radius: ${SHAPE.cardRadius}px`,
        `- Control radius: ${SHAPE.controlRadius}px`,
        `- Pill radius: ${SHAPE.pillRadius}`,
        `- Control height: ${SHAPE.controlHeight}px (sm ${SHAPE.controlHeightSm}px)`,
        `- Border: ${SHAPE.borderWidth}px`,
        `- Page max width: ${SHAPE.pageMax}px`,
        `- Sidebar width: ${SHAPE.sidebarWidth}px`,
        '',
        '## Components',
        '',
        'Import from `ui`. Pages should compose these rather than write their own.',
        '',
        ...COMPONENTS.flatMap((group) => [`### ${group.group}`, '', group.names.map((n) => `\`${n}\``).join(', '), '']),
        '## Page anatomy',
        '',
        '```tsx',
        'import { Page, PageHeader, Section, Card, DataTable, Button } from \'ui\'',
        '',
        'export const ExampleView = () => (',
        '    <Page>',
        '        <PageHeader title="View cases" actions={<Button variant="primary">Add</Button>} />',
        '        <Section title="Cases">',
        '            <DataTable columns={columns} rows={rows} rowKey={(r) => r.id} />',
        '        </Section>',
        '    </Page>',
        ')',
        '```',
        '',
    ].join('\n')
}

const xmlEscape = (value: string | number): string =>
    String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&apos;')

export const buildStyleGuideXml = (): string => {
    const colours = SWATCHES.map(
        (group) =>
            `    <group name="${xmlEscape(group.group)}">\n${group.items
                .map(([name, hex]) => `      <token name="${xmlEscape(name)}" hex="${xmlEscape(hex)}" />`)
                .join('\n')}\n    </group>`
    ).join('\n')

    const rules = USAGE_RULES.map(([topic, rule]) => `    <rule topic="${xmlEscape(topic)}">${xmlEscape(rule)}</rule>`).join('\n')
    const instructions = AGENT_INSTRUCTIONS.map((line) => `    <item>${xmlEscape(line)}</item>`).join('\n')
    const type = TYPE_SCALE.map((row) => `    <style role="${xmlEscape(row.label)}" spec="${xmlEscape(row.meta)}" sample="${xmlEscape(row.sample)}" />`).join(
        '\n'
    )
    const components = COMPONENTS.map(
        (group) =>
            `    <group name="${xmlEscape(group.group)}">\n${group.names
                .map((name) => `      <component name="${xmlEscape(name)}" />`)
                .join('\n')}\n    </group>`
    ).join('\n')

    return `<?xml version="1.0" encoding="UTF-8"?>
<styleGuide name="${xmlEscape(STYLE_GUIDE_TITLE)}" version="${xmlEscape(STYLE_GUIDE_VERSION)}" product="sentinel-dashboard" audience="agents">
  <agentInstructions>
${instructions}
  </agentInstructions>
  <rules>
${rules}
  </rules>
  <colours>
${colours}
  </colours>
  <typography font="${xmlEscape(SHAPE.font)}" mono="${xmlEscape(SHAPE.fontMono)}">
${type}
  </typography>
  <spacing scale="${SPACING_STEPS.join(',')}" cardRadius="${SHAPE.cardRadius}" controlRadius="${SHAPE.controlRadius}" pillRadius="${SHAPE.pillRadius}" controlHeight="${SHAPE.controlHeight}" borderWidth="${SHAPE.borderWidth}" pageMax="${SHAPE.pageMax}" />
  <components>
${components}
  </components>
</styleGuide>
`
}

const excelCell = (value: string | number, type: 'String' | 'Number' = 'String') =>
    `      <Cell><Data ss:Type="${type}">${xmlEscape(value)}</Data></Cell>`

const excelRow = (cells: string) => `    <Row>\n${cells}\n    </Row>`

const excelSheet = (name: string, header: string[], rows: Array<Array<string | number>>) => {
    const headerRow = excelRow(header.map((h) => excelCell(h)).join('\n'))
    const body = rows.map((row) => excelRow(row.map((cell) => excelCell(cell, typeof cell === 'number' ? 'Number' : 'String')).join('\n'))).join('\n')
    return `  <Worksheet ss:Name="${xmlEscape(name)}">
  <Table>
${headerRow}
${body}
  </Table>
 </Worksheet>`
}

export const buildStyleGuideExcel = (): string => {
    const colourRows = SWATCHES.flatMap((group) => group.items.map(([name, hex]) => [group.group, name, hex]))
    const typeRows = TYPE_SCALE.map((row) => [row.label, row.meta, row.sample])
    const componentRows = COMPONENTS.flatMap((group) => group.names.map((name) => [group.group, name]))

    return `<?xml version="1.0"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">
${excelSheet('Rules', ['Topic', 'Rule'], USAGE_RULES.map(([topic, rule]) => [topic, rule]))}
${excelSheet('Colours', ['Group', 'Token', 'Hex'], colourRows)}
${excelSheet('Typography', ['Role', 'Spec', 'Sample'], typeRows)}
${excelSheet('Spacing', ['Token', 'Value'], [
        ['spacingScale', SPACING_STEPS.join(', ')],
        ['cardRadius', SHAPE.cardRadius],
        ['controlRadius', SHAPE.controlRadius],
        ['pillRadius', SHAPE.pillRadius],
        ['controlHeight', SHAPE.controlHeight],
        ['controlHeightSm', SHAPE.controlHeightSm],
        ['borderWidth', SHAPE.borderWidth],
        ['pageMax', SHAPE.pageMax],
        ['sidebarWidth', SHAPE.sidebarWidth],
        ['font', SHAPE.font],
    ])}
${excelSheet('Components', ['Group', 'Name'], componentRows)}
</Workbook>
`
}

const pdfEscape = (text: string) => text.replace(/\\/g, '\\\\').replace(/\(/g, '\\(').replace(/\)/g, '\\)')

const wrapPdfLine = (text: string, max = 96): string[] => {
    const normalised = text.replace(/\t/g, '  ')
    if (normalised.length <= max) return [normalised]
    const words = normalised.split(/\s+/)
    const lines: string[] = []
    let current = ''
    for (const word of words) {
        if (!current) {
            current = word
            continue
        }
        if ((current + ' ' + word).length <= max) current += ` ${word}`
        else {
            lines.push(current)
            current = word
        }
    }
    if (current) lines.push(current)
    return lines
}

export const buildStyleGuidePdf = (): Uint8Array => {
    const source = buildStyleGuideMarkdown()
        .replace(/[`*_#|]/g, ' ')
        .replace(/ +/g, ' ')
        .split('\n')
        .flatMap((line) => wrapPdfLine(line.trim()))

    const pageWidth = 595.28
    const pageHeight = 841.89
    const margin = 48
    const fontSize = 9
    const lineHeight = 12
    const linesPerPage = Math.max(1, Math.floor((pageHeight - margin * 2) / lineHeight))
    const pages: string[][] = []
    for (let i = 0; i < source.length; i += linesPerPage) pages.push(source.slice(i, i + linesPerPage))
    if (pages.length === 0) pages.push([''])

    const objects: string[] = []
    objects.push('<< /Type /Catalog /Pages 2 0 R >>')
    const pageIds = pages.map((_, i) => 3 + i)
    const contentIds = pages.map((_, i) => 3 + pages.length + i)
    const fontId = 3 + pages.length * 2
    objects.push(`<< /Type /Pages /Kids [${pageIds.map((id) => `${id} 0 R`).join(' ')}] /Count ${pages.length} >>`)

    pages.forEach((_, i) => {
        objects.push(
            `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${pageWidth} ${pageHeight}] /Contents ${contentIds[i]} 0 R /Resources << /Font << /F1 ${fontId} 0 R >> >> >>`
        )
    })

    pages.forEach((lines) => {
        const stream = [
            'BT',
            `/F1 ${fontSize} Tf`,
            `${lineHeight} TL`,
            `${margin} ${pageHeight - margin} Td`,
            ...lines.map((line) => `(${pdfEscape(line)}) Tj T*`),
            'ET',
        ].join('\n')
        objects.push(`<< /Length ${stream.length} >>\nstream\n${stream}\nendstream`)
    })

    objects.push('<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>')

    let offset = 0
    const chunks: string[] = ['%PDF-1.4\n']
    offset = chunks[0].length
    const xref = [0]
    objects.forEach((obj, i) => {
        xref.push(offset)
        const block = `${i + 1} 0 obj\n${obj}\nendobj\n`
        chunks.push(block)
        offset += block.length
    })

    const xrefStart = offset
    const xrefTable = [
        'xref',
        `0 ${objects.length + 1}`,
        '0000000000 65535 f ',
        ...xref.slice(1).map((pos) => `${String(pos).padStart(10, '0')} 00000 n `),
        'trailer',
        `<< /Size ${objects.length + 1} /Root 1 0 R >>`,
        'startxref',
        String(xrefStart),
        '%%EOF',
    ].join('\n')
    chunks.push(xrefTable)

    const pdf = chunks.join('')
    const bytes = new Uint8Array(pdf.length)
    for (let i = 0; i < pdf.length; i += 1) bytes[i] = pdf.charCodeAt(i) & 0xff
    return bytes
}
